'use client';

/**
 * InlineSupplierLookup — a compact inline version of UnifiedContactLookup
 * scoped to supplier selection only. Provides the same core UX: debounced
 * supplier search, a selected-supplier chip, and inline supplier creation
 * via QuickContactModal. Used by bulk finance modals where the full
 * contact/location/person lookup would be excessive.
 */

import { useState, useEffect, useCallback } from 'react';
import { Search, Loader2, X, Building2, UserPlus } from 'lucide-react';
import { authService } from '@/lib/authService';
import QuickContactModal, { type QuickContactResult } from '@/components/contacts/QuickContactModal';

export interface InlineSupplierResult {
  id: string;
  display_name: string;
  email?: string;
  supplier_code?: string;
}

interface InlineSupplierLookupProps {
  /** Currently selected supplier (controlled) */
  value: InlineSupplierResult | null;
  onChange: (supplier: InlineSupplierResult | null) => void;
  isDark?: boolean;
  placeholder?: string;
  /** Show the "create new supplier" affordance (default true) */
  allowCreate?: boolean;
}

export function InlineSupplierLookup({
  value,
  onChange,
  isDark,
  placeholder = 'Search suppliers...',
  allowCreate = true,
}: InlineSupplierLookupProps) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<InlineSupplierResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  const runSearch = useCallback(async (query: string) => {
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const token = authService.getTokens()?.access_token;
      // Same params UnifiedContactLookup uses — /api/v1/contacts supports
      // contact_types/is_active; there is no `is_supplier` param (it was
      // silently ignored, returning every contact type).
      const params = new URLSearchParams({
        search: query,
        is_active: 'true',
        limit: '50',
        contact_types: 'supplier',
      });
      const res = await fetch(`/api/v1/contacts?${params}`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (res.ok) {
        const data = await res.json();
        const items: InlineSupplierResult[] = (data.data || data || []).map((c: Record<string, unknown>) => ({
          id: String(c.id || ''),
          display_name: String(c.company_name || c.contact_person || c.display_name || c.name || ''),
          email: c.email ? String(c.email) : undefined,
          supplier_code: c.supplier_code ? String(c.supplier_code) : undefined,
        }));
        setResults(items);
      }
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => runSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search, runSearch]);

  const handleCreated = useCallback(
    (result: QuickContactResult) => {
      setShowCreate(false);
      setSearch('');
      setResults([]);
      onChange({
        id: String(result.contact.id),
        display_name: result.contact.company_name,
        email: result.contact.email || undefined,
      });
    },
    [onChange],
  );

  const inputCls = isDark
    ? 'bg-gray-900 border-gray-700 text-gray-100 placeholder-gray-500'
    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400';

  return (
    <div className="space-y-2">
      {value ? (
        <div
          className="rounded-lg border p-3 flex items-center justify-between"
          style={{ borderColor: 'var(--theme-border)', backgroundColor: 'var(--theme-background)' }}
        >
          <div className="flex items-center gap-2 min-w-0">
            <Building2 className="h-4 w-4 text-teal-500 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{value.display_name}</p>
              {value.supplier_code && <p className="text-xs opacity-60">{value.supplier_code}</p>}
              {value.email && !value.supplier_code && <p className="text-xs opacity-60 truncate">{value.email}</p>}
            </div>
          </div>
          <button
            type="button"
            onClick={() => onChange(null)}
            className="p-1 rounded hover:bg-black/10 dark:hover:bg-white/10"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ) : (
        <>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-40" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={placeholder}
              className={`w-full rounded-lg border py-2 pl-9 pr-9 text-sm ${inputCls}`}
            />
            {loading && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin opacity-40" />
            )}
          </div>

          {results.length > 0 && (
            <div
              className="max-h-48 overflow-y-auto rounded-lg border"
              style={{ borderColor: 'var(--theme-border)' }}
            >
              {results.map((supplier) => (
                <button
                  key={supplier.id}
                  type="button"
                  onClick={() => {
                    onChange(supplier);
                    setSearch('');
                    setResults([]);
                  }}
                  className="w-full text-left px-3 py-2 hover:bg-black/5 dark:hover:bg-white/5 border-b last:border-b-0 transition-colors"
                  style={{ borderColor: 'var(--theme-border)' }}
                >
                  <p className="text-sm font-medium truncate">{supplier.display_name}</p>
                  {supplier.email && <p className="text-xs opacity-60 truncate">{supplier.email}</p>}
                </button>
              ))}
            </div>
          )}

          {search.length >= 2 && !loading && results.length === 0 && (
            <p className="text-sm opacity-50 text-center py-2">No suppliers found</p>
          )}

          {allowCreate && (
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300 transition-colors"
            >
              <UserPlus className="h-3.5 w-3.5" />
              Create new supplier
            </button>
          )}
        </>
      )}

      <QuickContactModal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={handleCreated}
        defaultContactType="supplier"
        contactTypes={['supplier', 'vendor']}
        isDark={isDark}
      />
    </div>
  );
}
